/**
 * StageEditorRowView — one editable stage row in the pipeline Stage editor
 * (SE-5488, restyled SE-6554).
 *
 * Pure presentation. The row reads as a card: the drag handle, stage dot, the
 * name over its glossary line, the protection badges, the occupancy count, and
 * an overflow menu. Under a flagged stage it renders the protection notice —
 * what the flags actually cost, in the same two numbers the delete guard uses
 * (cards held, rules referencing).
 *
 * ## The ⠿ handle is an AFFORDANCE, not a drag surface
 *
 * The design leads the row with a ⠿ grip, so that is what renders — but
 * nothing in this modal implements pointer drag-reorder, and the handle is
 * deliberately inert rather than a control that looks draggable and isn't. The
 * ACTION lives in the overflow menu ("Move up" / "Move down"), which is why the
 * handle carries no press target and no accessibility role: the menu rows are
 * the reachable, screen-reader-addressable path. Real drag-reorder inside the
 * editor is new machinery and is tracked as a follow-up.
 *
 * Rename / reorder / target / Sensitive / Terminal / Delete all live in the
 * overflow menu the design draws; nothing was dropped, and each menu row keeps
 * the testID its inline control carried so the existing contracts still
 * resolve — only the interaction that reaches them changed.
 *
 * The time target left the row entirely: an always-visible "Target [—]" box on
 * every row is not in the design and dominated rows whose target is unset,
 * which is most of them. It is now a menu row that reveals the field, and a
 * SET target reads as quiet text beside the count so the information is still
 * on the row at a glance.
 *
 * Pills and the stage dot use `rounded-lg` (12px) rather than a full-pill
 * radius: both are 20px tall or less, so the radius clamps to half the height
 * and renders identically, and it keeps this file off the
 * `roundedFullFeatureViews` budget list.
 * @module features/player-kanban/components/molecules/StageEditorRow/StageEditorRowView
 */

import {
  AlertTriangle,
  Check,
  Eye,
  LogOut,
  MoreHorizontal,
  Shield,
  X,
} from "lucide-react-native";
import type { LucideIcon } from "lucide-react-native";
import { memo } from "react";

import { TEST_ID_PLAYER_KANBAN } from "e2e/testId";

import { TONE_CLASS } from "@/components/atoms/Badge";
import { Box } from "@/components/atoms/Box";
import { Icon } from "@/components/atoms/Icon";
import { Input } from "@/components/atoms/Input";
import { Pressable } from "@/components/atoms/Pressable";
import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";
import DesktopDropdownMenu, {
  type DesktopDropdownMenuItem,
} from "@/components/molecules/DesktopDropdownMenu";

import StageTargetField from "../StageTargetField";

/** Props for {@link StageEditorRowView}. */
export interface StageEditorRowViewProps {
  /** Stage id (drives testIDs). */
  readonly stageId: string;
  /** Current stage name (display value when not renaming). */
  readonly name: string;
  /** The stage's glossary line ("" renders no description). */
  readonly description: string;
  /** Whether the stage is protected (sensitive — gates the reason prompt). */
  readonly isProtected: boolean;
  /** Whether the stage is terminal (a close stage). */
  readonly isTerminal: boolean;
  /** Whether automation rules reference this stage (drives the Guarded badge). */
  readonly isGuarded: boolean;
  /** How many cards sit in the stage (the count pill). */
  readonly cardCount: number;
  /** The protection notice, or "" when there is nothing to warn about. */
  readonly protectionNotice: string;
  /** SE-5497: the stage's configured time-in-stage target in days (null = none). */
  readonly timeTargetDays: number | null;
  /** Whether the inline rename input is active. */
  readonly isEditingName: boolean;
  /** The in-progress rename value (only meaningful while editing). */
  readonly editNameValue: string;
  /** Whether the revealed time-target editor is showing. */
  readonly isEditingTarget: boolean;
  /** Hide the time-target editor. */
  readonly onCloseTargetEditor: () => void;
  /** Whether a write for this row is in flight (disables controls). */
  readonly isBusy: boolean;
  /** The overflow-menu rows (rename, flags, delete), built by the container. */
  readonly menuItems: readonly DesktopDropdownMenuItem[];
  /** Whether the overflow menu is open. */
  readonly isMenuOpen: boolean;
  /** Toggle the overflow menu. */
  readonly onMenuToggle: () => void;
  /** Close the overflow menu. */
  readonly onMenuClose: () => void;
  /** Rename input change. */
  readonly onChangeName: (value: string) => void;
  /** Commit the rename. */
  readonly onSaveRename: () => void;
  /** Abandon the rename. */
  readonly onCancelRename: () => void;
  /** SE-5497: commit a new time-in-stage target (null clears it). */
  readonly onChangeTarget: (stageId: string, next: number | null) => void;
}

/**
 * The three protection badges, in the order the design lists them.
 *
 * `Sensitive` and `Terminal` take their pairings from the Badge atom's
 * `TONE_CLASS` (the only place raw shades may be spelled), so both invert
 * correctly in dark mode. `Guarded` is a neutral surface pairing and states its
 * semantic tokens directly.
 */
const BADGE_STYLE: Record<
  "guarded" | "sensitive" | "terminal",
  { readonly icon: LucideIcon; readonly box: string; readonly text: string }
> = {
  guarded: {
    icon: Shield,
    box: "bg-surface-raised",
    text: "text-content-secondary",
  },
  sensitive: {
    icon: Eye,
    box: TONE_CLASS["warning-subtle"].box,
    text: TONE_CLASS["warning-subtle"].text,
  },
  terminal: {
    icon: LogOut,
    box: "bg-status-error-surface",
    text: "text-status-error",
  },
};

/**
 * Render one protection badge.
 *
 * A plain render helper, not a component, so the file keeps one component and
 * the JSX below stays readable.
 * @param kind - Which badge to draw.
 * @param label - The badge's visible label.
 * @returns The badge element.
 */
const renderBadge = (kind: keyof typeof BADGE_STYLE, label: string) => {
  const style = BADGE_STYLE[kind];
  return (
    <Stack
      direction="horizontal"
      className={`items-center gap-1 rounded-lg px-2 py-1 ${style.box}`}
    >
      <Icon as={style.icon} size="2xs" className={style.text} />
      <Text variant="micro" className={style.text}>
        {label}
      </Text>
    </Stack>
  );
};

/**
 * Render one stage row.
 * @param props - Component props
 * @param props.stageId - Stage id (drives testIDs).
 * @param props.name - Current stage name (display value when not renaming).
 * @param props.description - The stage's glossary line ("" renders no description).
 * @param props.isProtected - Whether the stage is protected (sensitive — gates the reason prompt).
 * @param props.isTerminal - Whether the stage is terminal (a close stage).
 * @param props.isGuarded - Whether automation rules reference this stage (drives the Guarded badge).
 * @param props.cardCount - How many cards sit in the stage (the count pill).
 * @param props.protectionNotice - The protection notice, or "" when there is nothing to warn about.
 * @param props.timeTargetDays - SE-5497: the stage's configured time-in-stage target in days (null = none).
 * @param props.isEditingName - Whether the inline rename input is active.
 * @param props.editNameValue - The in-progress rename value (only meaningful while editing).
 * @param props.isEditingTarget - Whether the revealed time-target editor is showing.
 * @param props.onCloseTargetEditor - Hide the time-target editor.
 * @param props.isBusy - Whether a write for this row is in flight (disables controls).
 * @param props.menuItems - The overflow-menu rows (rename, flags, delete), built by the container.
 * @param props.isMenuOpen - Whether the overflow menu is open.
 * @param props.onMenuToggle - Toggle the overflow menu.
 * @param props.onMenuClose - Close the overflow menu.
 * @param props.onChangeName - Rename input change.
 * @param props.onSaveRename - Commit the rename.
 * @param props.onCancelRename - Abandon the rename.
 * @param props.onChangeTarget - SE-5497: commit a new time-in-stage target (null clears it).
 * @returns The row.
 */
const StageEditorRowView = ({
  stageId,
  name,
  description,
  isProtected,
  isTerminal,
  isGuarded,
  cardCount,
  protectionNotice,
  timeTargetDays,
  isEditingName,
  editNameValue,
  isEditingTarget,
  onCloseTargetEditor,
  isBusy,
  menuItems,
  isMenuOpen,
  onMenuToggle,
  onMenuClose,
  onChangeName,
  onSaveRename,
  onCancelRename,
  onChangeTarget,
}: StageEditorRowViewProps) => (
  <Stack
    className="w-full gap-2"
    testID={TEST_ID_PLAYER_KANBAN.stageEditorRowFor(stageId)}
  >
    <Stack
      direction="horizontal"
      className="w-full items-center gap-2 rounded-md border border-outline-default bg-surface-base p-2"
    >
      {/* The design's grip. Inert by design — see the module docblock: the
            reorder ACTION is a pair of menu rows, and a handle that looks
            draggable but is not would be the worse of the two lies. */}
      <Text variant="caption" className="shrink-0 text-content-muted">
        ⠿
      </Text>

      {/* Stage dot. Decorative and constant: stages carry no per-stage colour
            in the data model, so this is the SAME amber the board's column
            header draws before every stage name, not a data-driven swatch. */}
      <Box className="size-2 shrink-0 rounded-lg bg-status-warning" />

      {/* Name over its glossary line, or the inline rename input. */}
      <Box className="min-w-0 flex-1">
        {isEditingName ? (
          <Stack direction="horizontal" className="items-center gap-2">
            <Input
              className="flex-1 rounded-sm"
              field={{
                value: editNameValue,
                onChangeText: onChangeName,
                placeholder: "Stage name",
                testID:
                  TEST_ID_PLAYER_KANBAN.stageEditorRenameInputFor(stageId),
                accessibilityLabel: `Rename ${name}`,
              }}
            />
            <Pressable
              testID={TEST_ID_PLAYER_KANBAN.stageEditorRenameSaveFor(stageId)}
              accessibilityLabel={`Save ${name}`}
              disabled={isBusy}
              onPress={onSaveRename}
            >
              <Icon as={Check} size="sm" className="text-status-success" />
            </Pressable>
            <Pressable
              accessibilityLabel={`Cancel renaming ${name}`}
              disabled={isBusy}
              onPress={onCancelRename}
            >
              <Icon as={X} size="sm" className="text-status-error" />
            </Pressable>
          </Stack>
        ) : (
          <Stack className="min-w-0 gap-1">
            {/* 14px BOLD — `font-heading` is the Satoshi bold face. The
                  neighbouring `body-strong` bundle is the MEDIUM (500) face,
                  which is a weight short of what this row's spec asks for. */}
            <Text variant="body-bold" className="text-content-primary">
              {name}
            </Text>
            {description === "" ? null : (
              <Text variant="caption" className="text-content-muted">
                {description}
              </Text>
            )}
          </Stack>
        )}
      </Box>

      {/* Badges. `Guarded` marks a stage automation rules depend on — the
            same referencing set the delete guard pauses. */}
      <Stack direction="horizontal" className="shrink-0 items-center gap-1">
        {isGuarded ? renderBadge("guarded", "Guarded") : null}
        {isProtected ? renderBadge("sensitive", "Sensitive") : null}
        {isTerminal ? renderBadge("terminal", "Terminal") : null}
      </Stack>

      {/* SE-5497: a SET target reads here as quiet text; an unset one prints
            nothing at all. The editable field is a menu row away — it does not
            occupy every row just so the rare configured stage can show one
            number. */}
      {timeTargetDays === null ? null : (
        <Text
          variant="micro"
          className="shrink-0 text-content-muted"
          testID={TEST_ID_PLAYER_KANBAN.stageEditorTimeTargetValueFor(stageId)}
        >
          {`target ${timeTargetDays}d`}
        </Text>
      )}

      <Box className="shrink-0 rounded-lg bg-surface-raised px-2 py-micro">
        <Text variant="caption" className="text-content-primary">
          {cardCount}
        </Text>
      </Box>

      <DesktopDropdownMenu
        isOpen={isMenuOpen}
        onClose={onMenuClose}
        onToggle={onMenuToggle}
        placement="bottom-end"
        triggerTestID={TEST_ID_PLAYER_KANBAN.stageEditorMenuFor(stageId)}
        triggerAccessibilityLabel={`Options for ${name}`}
        triggerClassName="rounded-sm p-1"
        triggerContent={
          <Icon as={MoreHorizontal} size="xs" className="text-content-muted" />
        }
        items={menuItems}
      />
    </Stack>

    {/* Revealed by the "Set time target…" menu row. Keyed by the committed
          value so a successful write reseeds the draft via remount (no reset
          effect — `react-hooks/set-state-in-effect` forbids that form). */}
    {isEditingTarget ? (
      <Stack
        direction="horizontal"
        className="items-center gap-2 rounded-sm bg-surface-raised px-2 py-2"
      >
        <StageTargetField
          key={`${stageId}-${timeTargetDays ?? "none"}`}
          stageId={stageId}
          stageName={name}
          timeTargetDays={timeTargetDays}
          isBusy={isBusy}
          onChangeTarget={onChangeTarget}
        />
        <Pressable
          accessibilityLabel={`Close time target for ${name}`}
          onPress={onCloseTargetEditor}
        >
          <Icon as={X} size="xs" className="text-content-muted" />
        </Pressable>
      </Stack>
    ) : null}

    {protectionNotice === "" ? null : (
      <Stack
        direction="horizontal"
        className={`items-start gap-2 rounded-sm p-4 ${TONE_CLASS["warning-subtle"].box}`}
        testID={TEST_ID_PLAYER_KANBAN.stageEditorProtectionNoticeFor(stageId)}
      >
        <Icon
          as={AlertTriangle}
          size="2xs"
          className={TONE_CLASS["warning-subtle"].text}
        />
        <Text
          variant="caption"
          className={`flex-1 ${TONE_CLASS["warning-subtle"].text}`}
        >
          {protectionNotice}
        </Text>
      </Stack>
    )}
  </Stack>
);

StageEditorRowView.displayName = "StageEditorRowView";

export default memo(StageEditorRowView);
